import React from "react";
import { Controller } from "react-hook-form";

import type { BuildingOption } from "../../../../api/client/types/common.types";
import { Button } from "../../../../shared/components/ui/Button";
import { LoadingSpinner } from "../../../../shared/components/ui/LoadingSpinner";
import { FormField } from "../../../../shared/components/forms/FormField";
import { useManagementYearSummaryReportGenerator } from "./useManagementYearSummaryReportGenerator";
import {
  LABEL_BUILDING_REQUIRED,
  PLACEHOLDER_LOADING_BUILDINGS,
  PLACEHOLDER_SEARCH_BUILDING,
  LOADING_BUILDINGS,
  NO_BUILDINGS_AVAILABLE,
  BUTTON_GENERATING,
  BUTTON_GENERATE_INFORM,
  NOTE_LABEL,
} from "../../constants/reportMessages";

interface ManagementYearSummaryReportGeneratorProps {
  title: string;
  description: string;
  onGenerate: (year: number, building_id: string) => Promise<Blob | null>;
  /** Full list of all buildings (not filtered by date). */
  buildingOptions: BuildingOption[];
  /** Optional: true while the building list is loading. */
  isFetchingBuildings?: boolean;
}

/**
 * Report generator for the 3-page yearly management report PDF
 * (cover, yearly summary, closing).
 *
 * Uses react-hook-form with a numeric year field and a required building selector.
 */
export const ManagementYearSummaryReportGenerator: React.FC<
  ManagementYearSummaryReportGeneratorProps
> = ({
  title,
  description,
  onGenerate,
  buildingOptions,
  isFetchingBuildings = false,
}) => {
  const {
    form,
    values,
    errors,
    isValid,
    isSubmitting,
    handleGenerateReport,
  } = useManagementYearSummaryReportGenerator({ onGenerate, buildingOptions });

  const buildingSelectOptions: { value: string; label: string }[] =
    buildingOptions.map((b) => ({ value: b.id, label: b.name }));

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-xl font-semibold text-gray-800 mb-4">{title}</h2>

      <div className="space-y-6">
        <div className="space-y-4">
          <div className="max-w-xs">
            <FormField
              type="input"
              label="Año (YYYY)"
              required
              inputProps={{
                value: values.year,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                  form.setValue("year", e.target.value, {
                    shouldValidate: false,
                  }),
                type: "number",
                min: 2000,
                max: 2100,
                placeholder: "Ej: 2025",
                className: "border rounded-md p-2 w-full text-sm",
              }}
              error={errors.year}
            />
          </div>

          <div className="max-w-md">
            <Controller
              control={form.control}
              name="building_id"
              render={({ field }) => (
                <FormField
                  type="searchableSelect"
                  label={LABEL_BUILDING_REQUIRED}
                  searchableSelectProps={{
                    id: "building-management-year-summary",
                    options: buildingSelectOptions,
                    value: field.value,
                    onChange: field.onChange,
                    placeholder: isFetchingBuildings
                      ? PLACEHOLDER_LOADING_BUILDINGS
                      : PLACEHOLDER_SEARCH_BUILDING,
                    disabled: isFetchingBuildings,
                  }}
                  error={errors.building_id}
                  required
                  className="gap-0"
                />
              )}
            />
            {isFetchingBuildings && (
              <div className="mt-1 text-sm text-blue-600 flex items-center gap-2">
                <LoadingSpinner size="sm" />
                {LOADING_BUILDINGS}
              </div>
            )}
            {!isFetchingBuildings &&
              buildingOptions.length === 0 &&
              !errors.building_id && (
                <p className="mt-1 text-sm text-gray-500">
                  {NO_BUILDINGS_AVAILABLE}
                </p>
              )}
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button
            onClick={handleGenerateReport}
            disabled={isSubmitting || !isValid}
            className="w-full sm:w-auto min-w-[200px]"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <LoadingSpinner size="sm" />
                {BUTTON_GENERATING}
              </span>
            ) : (
              BUTTON_GENERATE_INFORM
            )}
          </Button>
        </div>
      </div>

      <div className="mt-4 p-4 bg-blue-50 rounded-md">
        <p className="text-sm text-gray-700">
          <strong>{NOTE_LABEL}:</strong> {description}
        </p>
      </div>
    </div>
  );
};

